import React, { useCallback, useEffect, useState } from 'react';
import Image from '@/components/atoms/Image';
import { InformationCircleIcon, ShareIcon, StarIcon } from '@heroicons/react/24/outline';
import { ProductTileProps } from '@/components/molecules/product-tile/types';
import useDiscount from '@/hooks/useDiscount';
import useFormat from '@/hooks/useFormat';
import useTranslation from '@/providers/I18n/hooks/useTranslation';
import DiscountTag from '@/components/molecules/product-tile/components/discount-tag';
import Button from '@/components/atoms/button';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import Link from 'next/link';
import useClassNames from '@/helpers/hooks/useClassNames';
import { useParams } from 'next/navigation';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { sdk } from '@/sdk/CommercetoolsSDK';
import { exclusivityMapping } from '@/constants/exclusivityMapping';
import { getLifecycleBadge, getIntroductoryPriceBadge } from '@/utils/product-lifecycle';

const ProductGridTile = (props: ProductTileProps) => {
  const { item, onAddToCart, onAddToProject, parent, buttonClassName } = props;
  const {
    name,
    description,
    inStock,
    sku,
    models,
    price,
    discountedPrice,
    currency,
    image,
    maxQuantity,
    restockableInDays,
    url,
    category,
    exclusiveProduct,
    productType,
    isNew,
    isBuilderRange,
    lifecycle,
  } = item;
  const lifecycleBadge = React.useMemo(() => getLifecycleBadge(lifecycle), [lifecycle]);
  const introPriceBadge = React.useMemo(
    () => getIntroductoryPriceBadge(models) || getIntroductoryPriceBadge(sku),
    [models, sku],
  );
  const { locale } = useParams();
  const { translate } = useTranslation();
  const { formatCurrency } = useFormat();
  const { isDiscounted, discountPercentage } = useDiscount(price, discountedPrice);
  const [target, setTarget] = useState('_blank');
  const { selectedBusinessUnit } = useStoreAndBusinessUnits();
  const variantSku = String(item?.variants?.[0]?.sku ?? '').trim();
  const [wishlistedSkus, setWishlistedSkus] = useState<string[]>([]);
  const [wishlistLoading, setWishlistLoading] = useState(true);

  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        const storeKey = locale === 'au' ? 'plumbershub-au' : 'plumbershub-nz';
        const response = await sdk.composableCommerce.custom.getCustomerWishlist({
          pageNumber: 1,
          pageSize: 100,
          storeKey,
        });

        if (!response.isError && response.data?.data?.items) {
          const skus = response.data.data.items.map((item: any) => String(item.variantsku).trim());

          setWishlistedSkus(skus);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setWishlistLoading(false);
      }
    };

    fetchWishlist();
  }, []);
  const initialIsWishlisted = wishlistedSkus?.includes(variantSku) ?? false;

  const [isWishlisted, setIsWishlisted] = useState(initialIsWishlisted);

  useEffect(() => {
    setIsWishlisted(initialIsWishlisted);
  }, [initialIsWishlisted]);
  const handleAddToWishlist = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation
    e.stopPropagation();
    try {
      const storeKey = locale === 'au' ? 'plumbershub-au' : 'plumbershub-nz';
      const sku = item?.variants?.[0]?.sku;
      if (isWishlisted) {
        const response = await sdk.composableCommerce.custom.removeProductFromWishlist({ variantSku: sku, storeKey });
        if (!response.isError) {
          setIsWishlisted(false);
        }
        return;
      }
      const response = await sdk.composableCommerce.custom.addProductToWishlist(
        JSON.stringify({ variantSku: sku, storeKey }),
      );
      if (!response.isError) {
        setIsWishlisted(true);
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
    }
  };
  const isFormaRange = React.useMemo(() => {
    const checkRange = (val: string | any) => {
      if (typeof val === 'string') {
        const v = val.toLowerCase();
        return v === 'forma' || v === 'forma ii';
      }
      if (val && typeof val === 'object' && val['en-AU']) {
        const v = val['en-AU'].toLowerCase();
        return v === 'forma' || v === 'forma ii';
      }
      return false;
    };
    const prod = item as any;
    return (
      checkRange(prod?.attributesAlgolia?.['range']) ||
      checkRange(prod?.attributes?.['range']) ||
      checkRange(prod?.variants?.[0]?.attributes?.['range'])
    );
  }, [item]);
  const isPLUMWO = React.useMemo(() => {
    const prod = item as any;
    const extractAttr = (val: any): any => {
      if (!val) return val;
      if (Array.isArray(val)) return val[0];
      if (typeof val === 'object' && val !== null) return val.key || val.label || val;
      return val;
    };
    const exclusiveProductNZ =
      extractAttr(prod?.attributesAlgolia?.['exclusive-product-NZ']) ||
      extractAttr(prod?.attributes?.['exclusive-product-NZ']) ||
      extractAttr(prod?.variants?.[0]?.attributes?.['exclusive-product-NZ']);
    return exclusiveProductNZ === 'PLUMWO';
  }, [item]);
  const exclusiveProductList = React.useMemo(() => {
    const prod = item as any;

    const toArray = (val: any): string[] => {
      if (!val) return [];
      if (Array.isArray(val)) return val;

      if (typeof val === 'object') {
        const extracted = val.key || val.label || val;
        return extracted ? [extracted] : [];
      }

      return [val];
    };
    let attributeKey = '';
    if (locale === 'nz') {
      attributeKey = 'exclusive-product-NZ';
    } else if (locale === 'au') {
      attributeKey = 'exclusive-product-AU';
    }

    if (!attributeKey) return [];

    const sources = [
      prod?.attributesAlgolia?.[attributeKey],
      prod?.attributes?.[attributeKey],
      prod?.variants?.[0]?.attributes?.[attributeKey],
    ];

    for (const src of sources) {
      const arr = toArray(src);
      if (arr.length > 0) return arr;
    }

    return [];
  }, [item, locale]);

  // const updateTarget = useCallback(() => {
  //   if (window.innerWidth <= 480) {
  //     setTarget('_self');
  //   } else {
  //     setTarget('_blank');
  //   }
  // }, []);

  // useEffect(() => {
  //   updateTarget(); // Update target on initial render
  //   window.addEventListener('resize', updateTarget);
  //   return () => window.removeEventListener('resize', updateTarget);
  // }, [updateTarget]  );
  const renderAddToProject = (additionalClassName?: string) => {
    const buttonClasses = useClassNames([
      'cursor-pointer md:min-w-[140px] rounded !bg-[#A6C9CC] !px-2 py-[10px] text-16 !font-semibold text-black',
      additionalClassName,
    ]);

    return !isBuilderRange ? (
      <div className="pt-3">

      <Button
        variant="ghost"
        type="submit"
        className={buttonClasses }
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onAddToProject && onAddToProject();
        }}
        >
        Add to Project
      </Button>
        </div>
    ) : (
        <>
          <Button
            variant="ghost"
            type="submit"
            className="flex cursor-pointer my-2 w-full bg-[#D9EFF1] items-center  gap-2 !rounded-none !px-0 py-[2px] !text-12 font-medium "
          >
            <svg
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="0"
              viewBox="0 0 512 512"
              className="text-[20px] md:text-[20px] 3xl:text-[20px]"
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M256 8C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm0 110c23.196 0 42 18.804 42 42s-18.804 42-42 42-42-18.804-42-42 18.804-42 42-42zm56 254c0 6.627-5.373 12-12 12h-88c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h12v-64h-12c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h64c6.627 0 12 5.373 12 12v100h12c6.627 0 12 5.373 12 12v24z"></path>
            </svg>
            <span className="text-left !text-[13px] md:pt-0">
              
              <Link href="/contact-us/">
                <span className=" underline ">Contact Us</span>{' '}
              </Link>
              about this product
            </span>
          </Button>
          <Button
            variant="ghost"
            type="submit"
            className={buttonClasses}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAddToProject && onAddToProject();
            }}
          >
            Add to Project
          </Button>
        </>
    );
  };
  const isExclusive = exclusiveProductList?.filter((p) => p && String(p).trim().toLowerCase() !== 'null')?.length > 0;
  const badgeTopClass = isExclusive
    ? isFormaRange || isPLUMWO
      ? 'top-10'
      : 'top-8'
    : isFormaRange || isPLUMWO
      ? 'top-6'
      : 'top-3';
  return (
    <div className={`${parent === 'project-builder' ? 'group' : ''}`}>
      <Link href={url ?? '#'}>
        <div className="relative">
          <div
            className="relative w-full"
            // onMouseEnter={() => setImageHovered(true)}
            // onMouseLeave={() => setImageHovered(false)}
          >
            <div className="relative block w-full xl:h-[10rem]" style={{ paddingBottom: '100%' }}>
              {exclusiveProductList
                ?.filter((p) => p && String(p).trim().toLowerCase() !== 'null')
                .map((p, index) => {
                  const partnerName = exclusivityMapping[p?.toUpperCase()] || String(p).toUpperCase();
                  return (
                    <div
                      key={index}
                      className="absolute bottom-2 left-2 z-20 origin-bottom-left scale-[0.65] whitespace-nowrap border border-white bg-[#A6C9CC] px-1 py-0.5 font-sans text-[15px] font-semibold text-black shadow-sm sm:scale-75"
                    >
                      EXCLUSIVE TO {partnerName}
                    </div>
                  );
                })}
              {/* {isNew && !lifecycleBadge && (
                <div className={`absolute left-3 ${isExclusive ? 'mt-3' : ''} ${badgeTopClass} px-2.5 py-1 rounded-[10px] text-white bg-[#FA2727] z-10 flex justify-center items-center text-[11px] font-semibold uppercase shadow-sm`}>
                  NEW
                </div>
              )} */}
              {(lifecycleBadge || introPriceBadge) && (
                <div className="absolute left-1 top-1 z-10 flex flex-wrap items-center gap-1">
                  {lifecycleBadge && (
                    <div
                      className="flex items-center justify-center rounded-[10px] px-2.5 py-1 text-[11px] font-semibold uppercase text-white shadow-sm"
                      style={{ backgroundColor: lifecycleBadge.color }}
                    >
                      {lifecycleBadge.label}
                    </div>
                  )}
                  {introPriceBadge && (
                    <div
                      className="flex items-center justify-center rounded-[10px] px-2.5 py-1 text-[11px] font-semibold uppercase text-white shadow-sm"
                      style={{ backgroundColor: introPriceBadge.color }}
                    >
                      {introPriceBadge.label}
                    </div>
                  )}
                </div>
              )}
              {image ? (
                <Image
                  priority
                  src={Array.isArray(image) ? image[0] : image}
                  alt={name ?? ''}
                  style={{ objectPosition: 'center' }}
                  className="w-full rounded-sm group-hover:opacity-75"
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />
              ) : (
                <Image
                  src="/images/No_product_Image.jpg"
                  className="absolute w-full rounded-sm group-hover:opacity-75"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  alt="empty product image"
                />
              )}
            </div>
          </div>
        </div>
        <div
          className={`${parent === 'project-builder' ? 'bg-[#F8F8F8] group-hover:bg-[#F3EDE9]' : ''} px-3 py-3 xl:px-5`}
        >
          <div className={`${parent === 'project-builder' ? 'min-h-[13rem]' : 'min-h-[13rem]'} flex flex-col`}>
            <div className="text-sm font-normal leading-5 text-[#333333]">{category}</div>
            <div className="mt-1 min-h-[80px] w-full">
              <p
                className={`${parent === 'project-builder' ? 'font-semibold' : 'font-medium'} lg:text-primary-black text-18 capitalize leading-[1.5rem] text-[#2B2C39] md:text-16 2xl:text-18`}
              >
                {name}
              </p>
            </div>
            <div className="mt-2 flex flex-col gap-1">
              {!isBuilderRange && (
                <div
                  className={`flex whitespace-nowrap leading-6 text-[#090909] sm:text-sm sm:font-light 2xl:text-base 2xl:font-normal ${
                    lifecycle?.toUpperCase()?.trim() === 'PROTOTYPE' ? 'invisible' : ''
                  }`}
                >
                  RRP
                  <span className="ml-2 font-semibold">{formatCurrency(price, currency)}</span>
                </div>
              )}
              {models && (
                <p className="flex gap-2 font-medium leading-5 text-[#666666] sm:text-sm 2xl:text-16">
                  Code <span className="font-semibold text-[#090909]">{models}</span>
                </p>
              )}
            </div>
            <div>
              <p
                className="mt-1 flex cursor-pointer gap-2 font-medium leading-6 text-[#666666] sm:text-sm 2xl:text-16"
                onClick={handleAddToWishlist}
              >
                <span className="cursor-pointer pt-2">Add to Saved Products</span>
                <span className="cursor-pointer font-semibold text-[#090909]">
                  <button className="cursor-pointer rounded-full p-1 transition-colors" aria-label="Add to wishlist">
                    {isWishlisted ? (
                      <StarIconSolid className="h-6 w-6 text-gray-800" />
                    ) : (
                      <StarIcon className="h-6 w-6 text-gray-600" />
                    )}
                  </button>
                </span>
              </p>
            </div>
            <div className="">{renderAddToProject('w-full font-semibold')}</div>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default ProductGridTile;
